import { ref, markRaw } from 'vue';

// One schema-agnostic filter-context factory. Replaces FilterItemsContext and
// FilterStatusHistoryContext: the mechanism (a temporary "context" table of key
// tuples, one `filter_view_<visualId>` per registered visual, INTERSECT to
// combine, derived views over the result) is identical between the two; only
// the schema differs, and that now arrives as configuration from the app
// (e.g. `apps/project-overview/src/data/filters.js`). This file holds ZERO project SQL.
//
// Parity with the two deleted classes is enforced by
// `tooling/parity/filter-context/run.mjs` (17 scenarios × 2 configs, normalised
// SQL). Two differences are intentional and asserted there:
//   1. init emits every DROP VIEW before the DROP TABLE (the history class
//      interleaved one of them); same statements, safer order.
//   2. executeFilteredQuery joins THIS context's table via USING(<keys>). The
//      history class joined `filter_context` (the items table) with three
//      history columns — a guaranteed Binder Error that conn.query swallowed.
//      It has no callers, so the fix is unobservable.
// The same harness drives issue-analysis's two configs
// (`apps/issue-analysis/src/data/filters.js`) against ITS two deleted classes
// (IA_ABSORPTION_PLAN Phase 4 step 11), with that fork's differences named
// there. `detect` exists for issue-analysis: its `issues` table's columns
// depend on the API version and on which field tags the project uses.
//
// Three extras exist for asset-management (AM_ABSORPTION_PLAN step B1), whose
// own class had them; all three are opt-in or inert, so a context that uses
// none of them emits exactly the SQL, ticks and debug info it always did:
//   - LABELS: an optional last argument on updateFilterWithQuery/updateFilter,
//     read back through getActiveFilters() (the ActiveFiltersBar chips). Not
//     config: an unlabelled call stores nothing and getFilterDebugInfo() never
//     reports labels.
//   - `serialize: true`: every mutator runs through one promise chain, so two
//     un-awaited clicks cannot interleave their DELETE/INSERT on the context
//     table (internal calls bypass the chain — a queued task awaiting a queued
//     method would wait on itself).
//   - `dropStaleViews: true`: init and resetFilters() first drop every
//     registered filter's view and backing table.

import { escapeSql } from '@rdrive/helper-sql';

/**
 * Reads one key off a record. Bare scalars are accepted so single-key contexts
 * keep working with `updateFilter(visualId, ['id1', 'id2'])`.
 * @param {unknown} rec
 * @param {string} name
 */
const readKey = (rec, name) => (rec !== null && typeof rec === 'object' ? rec[name] : rec);

/**
 * @typedef {{ name: string, type: string }} KeyColumn
 * @typedef {{ contextTable: string, sourceTable: string, keys: KeyColumn[], keyList: string,
 *   detected: any }} ViewArg  `detected` = this init's `detect` result (null without `detect`)
 * @typedef {{ name: string, select: (arg: ViewArg) => string }} ViewDef
 * @typedef {{ conn: object, sourceTable: string, options: object }} DetectArg
 */

/**
 * @typedef {object} FilterContextConfig
 * @property {object | (() => object)} conn  DuckDB connection, or a getter read
 *   on every call (recommended — the connection is a stub before
 *   `DuckDBService.initialize()`).
 * @property {string} id                     Used for defaults, logs and debug info.
 * @property {string} sourceTable            Table the base key set comes from.
 * @property {(string | KeyColumn)[]} keyColumns  Bare strings default to VARCHAR.
 * @property {string} [contextTable]         Default `${id}_filter_context`.
 * @property {boolean} [uniqueIndex]         Creates `${contextTable}_idx`.
 * @property {ViewDef[]} [views]             Views rebuilt on every init.
 * @property {(options: object, detected: any) => (string | null)} [baseSelect]
 *   Narrower base population; `null` ⇒ `SELECT DISTINCT <keys> FROM
 *   <sourceTable>`. A non-null result is ALSO pushed into the INTERSECT, so the
 *   restriction survives filter views that read the raw source table.
 * @property {(arg: DetectArg) => any} [detect]  Runtime detection, run at the
 *   START of every `initializeFilterContext()` (awaited), before any SQL of the
 *   factory's own: the app inspects the loaded source — with its own SQL and
 *   connection method (e.g. which optional columns exist) — and returns
 *   anything. The result is kept as `ctx.detected` and handed to every
 *   `views[].select` (as `detected`) and to `baseSelect` (second argument), so
 *   the derived views can follow the data. If it throws, init rejects with no
 *   query of its own, no tick, the registries and `detected` untouched.
 *   Absent ⇒ never awaited, `detected` stays null.
 * @property {number} [batchSize]            Rows per INSERT, default 200000.
 * @property {boolean} [serialize]  Default false. When true, every mutator —
 *   initializeFilterContext, updateFilterWithQuery, updateFilter, removeFilter,
 *   resetFilters, applyFiltersWithSQL — joins ONE promise chain: a call runs
 *   only after every earlier call has settled (fulfilled or rejected), so two
 *   un-awaited mutations cannot interleave their statements. Each call still
 *   returns its own promise (rejecting if its own task did); the
 *   `initialized` guard is checked when the task RUNS, so a mutation called
 *   while init is in flight runs after it, on the fresh context. Reads
 *   (getFilteredKeys, executeFilteredQuery, getFilterDebugInfo,
 *   getActiveFilters) are never queued.
 * @property {boolean} [dropStaleViews]  Default false. When true,
 *   initializeFilterContext() — after `detect`, before its DDL — runs one
 *   `DROP VIEW IF EXISTS filter_view_<id>; DROP TABLE IF EXISTS
 *   temp_filter_<id>;` per filter still registered, and the public
 *   resetFilters() first drops each registered filter's view and backing table
 *   (two queries each, as removeFilter does). Only this context's registered
 *   ids are dropped (`filter_view_` is one namespace for every context on the
 *   connection). The resets the mutators run themselves (removeFilter's last
 *   remove, an apply with nothing registered, the apply's error path) drop
 *   nothing, as in the class this replaces.
 */

/**
 * @typedef {{ visualId: string, label: any, count: number }} ActiveFilter
 *   `label` = the label of the call that registered the filter, or null;
 *   `count` = its match count as a JS Number (the registry keeps DuckDB's
 *   BigInt).
 */

/**
 * @typedef {object} FilterContext
 * @property {import('vue').Ref<Date>} filtersLastUpdated  Ticks with a FRESH
 *   Date object; the base chart compares it by identity, so a numeric timestamp
 *   would collide within a millisecond.
 * @property {Record<string, number>} filters        visualId → match count (live, markRaw).
 * @property {Record<string, string>} filterQueries  visualId → SQL (live, markRaw).
 * @property {boolean} initialized
 * @property {any} detected  the last init's `detect` result (null before / without
 *   `detect`). A plain property of a markRaw object: NOT reactive.
 * @property {() => ActiveFilter[]} getActiveFilters  a snapshot of the registered
 *   filters in registration order (not reactive — re-read on a tick).
 */

/**
 * @param {FilterContextConfig} config
 * @returns {FilterContext & Record<string, Function>}
 */
export function createFilterContext({
  conn,
  id,
  sourceTable,
  keyColumns,
  contextTable = `${id}_filter_context`,
  uniqueIndex = false,
  views = [],
  baseSelect = () => null,
  detect = null,
  batchSize = 200000,
  serialize = false,
  dropStaleViews = false,
}) {
  const keys = keyColumns.map((k) =>
    typeof k === 'string' ? { name: k, type: 'VARCHAR' } : { type: 'VARCHAR', ...k },
  );
  const keyList = keys.map((k) => k.name).join(', ');
  const columnDefs = keys.map((k) => `${k.name} ${k.type}`).join(', ');
  // Read per call: the DuckDB connection is a stub until initialize() runs.
  const getConn = typeof conn === 'function' ? conn : () => conn;
  const defaultBase = `SELECT DISTINCT ${keyList} FROM ${sourceTable}`;
  // Non-null ⇔ the base is narrower than sourceTable ⇒ re-applied in INTERSECT.
  let restrictedBase = null;
  const baseQuery = () => restrictedBase || defaultBase;

  const ctx = markRaw({
    id,
    contextTable,
    keyColumns: keys.map((k) => k.name),
    options: {},
    filterQueries: markRaw({}), // Store the actual queries
    filters: markRaw({}), // Visual metadata
    filtersLastUpdated: ref(new Date()),
    // Mutators are no-ops until initializeFilterContext() has run: init wipes
    // the registry and ticks filtersLastUpdated, so callers re-register on that
    // tick, never pre-init.
    initialized: false,
    detected: null,
  });

  // Labels (visualId → label), read only by getActiveFilters(): never in the SQL, the registries
  // or getFilterDebugInfo(), so a caller that passes none sees no difference. Stored only when
  // given (undefined / null / '' = none); re-registering an id without one clears its label.
  // Wiped with the registries (init, reset) and on removeFilter.
  let labels = new Map();
  const setLabel = (visualId, label) => {
    if (label === undefined || label === null || label === '') labels.delete(visualId);
    else labels.set(visualId, label);
  };

  // The mutators. Each one is installed on `ctx` below — as is, or behind the `serialize` queue —
  // and every internal call goes to these functions, never to the public (possibly queued) methods.
  const initializeFilterContext = async (options = {}) => {
    const c = getConn();
    ctx.options = { ...options };
    // Only awaited when configured: without `detect`, init runs exactly as it
    // always did (no extra await before its one query).
    if (detect) ctx.detected = await detect({ conn: c, sourceTable, options: ctx.options });
    restrictedBase = baseSelect(ctx.options, ctx.detected) || null;

    // `dropStaleViews`: a previous load's filter views may read columns a reload removed. One
    // batch per id still registered — after `detect` (a failing detect leaves them, with the
    // registry), before the DDL.
    if (dropStaleViews) {
      for (const visualId of Object.keys(ctx.filters)) {
        await c.query(`
          DROP VIEW IF EXISTS filter_view_${visualId};
          DROP TABLE IF EXISTS temp_filter_${visualId};
        `);
      }
    }

    const viewArg = { contextTable, sourceTable, keys, keyList, detected: ctx.detected };
    // Every DROP VIEW before the DROP TABLE — views depend on the table.
    const dropViews = views.map((v) => `DROP VIEW IF EXISTS ${v.name};`).join('\n      ');
    const createViews = views
      .map((v) => `CREATE VIEW ${v.name} AS ${v.select(viewArg)};`)
      .join('\n\n      ');
    const index = uniqueIndex
      ? `CREATE UNIQUE INDEX ${contextTable}_idx ON ${contextTable}(${keyList});`
      : '';

    await c.query(`
      ${dropViews}
      DROP TABLE IF EXISTS ${contextTable};

      CREATE TEMPORARY TABLE ${contextTable}(${columnDefs});
      ${index}

      INSERT INTO ${contextTable}
      ${baseQuery()};

      ${createViews}
    `);

    ctx.filters = markRaw({});
    ctx.filterQueries = markRaw({});
    labels = new Map();
    ctx.filtersLastUpdated.value = new Date();
    ctx.initialized = true; // unconditional: conn.query swallows SQL errors
  };

  const updateFilterWithQuery = async (visualId, query, label) => {
    if (!ctx.initialized) return;
    const c = getConn();

    try {
      // Register the query as a view for this filter
      await c.query(`
        DROP VIEW IF EXISTS filter_view_${visualId};
        CREATE TEMPORARY VIEW filter_view_${visualId} AS (
          SELECT DISTINCT ${keyList} FROM (${query})
        );
      `);

      ctx.filterQueries[visualId] = query;

      // conn.query swallows SQL errors and returns an empty result, so a
      // missing row means the view was never created — bail out loudly
      // instead of registering a broken filter.
      const countResult = await c.query(`SELECT COUNT(*) as count FROM filter_view_${visualId}`);
      const countRows = countResult.toArray();
      if (countRows.length === 0) {
        delete ctx.filterQueries[visualId];
        console.error(
          `Filter view for "${visualId}" could not be created; filter not applied. Query:\n${query}`,
        );
        return; // no tick, no registration
      }
      ctx.filters[visualId] = countRows[0].count;
      setLabel(visualId, label);

      await applyFiltersWithSQL();
    } catch (error) {
      console.error('Error updating filter with query:', error);
    }
  };

  const applyFiltersWithSQL = async () => {
    if (!ctx.initialized) return;
    const c = getConn();
    const filterIds = Object.keys(ctx.filters);

    if (filterIds.length === 0) {
      await resetFilters();
      return;
    }

    try {
      // Filter views may select from the raw source table, so a restricted
      // base is intersected back in to keep its restriction applied.
      const selects = filterIds.map((fid) => `SELECT ${keyList} FROM filter_view_${fid}`);
      if (restrictedBase) selects.push(restrictedBase);

      await c.query(`
        DELETE FROM ${contextTable};
        INSERT INTO ${contextTable}
        ${selects.join(' INTERSECT ')};
      `);

      ctx.filtersLastUpdated.value = new Date();
    } catch (error) {
      console.error('Error applying filters with SQL:', error);
      await resetFilters();
    }
  };

  const updateFilter = async (visualId, records, label) => {
    if (!ctx.initialized) return;
    const c = getConn();

    ctx.filters[visualId] = records.length;
    setLabel(visualId, label);

    if (records.length === 0) {
      const nulls = keys.map((k) => `CAST(NULL AS ${k.type}) AS ${k.name}`).join(', ');
      await c.query(`
        DROP VIEW IF EXISTS filter_view_${visualId};
        CREATE TEMPORARY VIEW filter_view_${visualId} AS
        SELECT ${nulls} WHERE 1=0;
      `);
    } else {
      const tempTable = `temp_filter_${visualId}`;

      await c.query(`DROP TABLE IF EXISTS ${tempTable}`);
      await c.query(`CREATE TEMPORARY TABLE ${tempTable}(${columnDefs})`);

      for (let i = 0; i < records.length; i += batchSize) {
        const chunk = records
          .slice(i, i + batchSize)
          .map((rec) => `(${keys.map((k) => `'${escapeSql(readKey(rec, k.name))}'`).join(', ')})`)
          .join(',');

        if (chunk) {
          await c.query(`INSERT INTO ${tempTable} VALUES ${chunk}`);
        }
      }

      // The table must outlive the view: views bind lazily, so dropping it
      // here would break every later read of the view. It is dropped when the
      // filter is removed or replaced.
      await c.query(`
        DROP VIEW IF EXISTS filter_view_${visualId};
        CREATE TEMPORARY VIEW filter_view_${visualId} AS
        SELECT DISTINCT ${keyList} FROM ${tempTable};
      `);
    }

    await applyFiltersWithSQL();
  };

  const removeFilter = async (visualId) => {
    if (!ctx.initialized) return;
    // Check with `in`: a registered filter matching zero rows has count 0,
    // which is falsy but must still be removable
    if (!(visualId in ctx.filters)) return;

    const c = getConn();

    await c.query(`DROP VIEW IF EXISTS filter_view_${visualId}`);
    await c.query(`DROP TABLE IF EXISTS temp_filter_${visualId}`);

    delete ctx.filters[visualId];
    delete ctx.filterQueries[visualId];
    labels.delete(visualId);

    if (Object.keys(ctx.filters).length === 0) {
      await resetFilters();
    } else {
      await applyFiltersWithSQL();
    }
  };

  const resetFilters = async () => {
    if (!ctx.initialized) return;
    const c = getConn();

    await c.query(`
      DELETE FROM ${contextTable};
      INSERT INTO ${contextTable}
      ${baseQuery()};
    `);

    // Replaced, not cleared: useStatusTypeFilters' tick watcher reads
    // `ctx.filters` live, and a fresh object is how it sees the wipe.
    ctx.filters = markRaw({});
    ctx.filterQueries = markRaw({});
    labels = new Map();
    ctx.filtersLastUpdated.value = new Date();
  };

  // `dropStaleViews`: the PUBLIC resetFilters() ("clear all") first drops every registered
  // filter's view and backing table — after the reset the registry is empty, so a later init could
  // no longer find them. The resets the mutators run themselves stay plain `resetFilters` (by then
  // the registry is empty; in the apply's error path the class this replaces did not drop either).
  const resetFiltersDroppingViews = async () => {
    if (!ctx.initialized) return;
    const c = getConn();
    for (const visualId of Object.keys(ctx.filters)) {
      await c.query(`DROP VIEW IF EXISTS filter_view_${visualId}`);
      await c.query(`DROP TABLE IF EXISTS temp_filter_${visualId}`);
    }
    await resetFilters();
  };

  // `serialize`: one promise chain for every mutator (standalone AM's `_enqueue`). A task starts
  // once the previous one has settled; the chain itself never rejects, so a failed task does not
  // block the next, and each caller still gets its own task's result or rejection. Off (the
  // default), the mutators are installed as they are — not wrapped.
  let chain = Promise.resolve();
  const enqueue = (task) => {
    const run = chain.then(task);
    chain = run.catch(() => {});
    return run;
  };
  const queued = (fn) => (serialize ? (...args) => enqueue(() => fn(...args)) : fn);

  ctx.initializeFilterContext = queued(initializeFilterContext);
  /** `(visualId, query, label?)` — `label`: see getActiveFilters(). */
  ctx.updateFilterWithQuery = queued(updateFilterWithQuery);
  ctx.applyFiltersWithSQL = queued(applyFiltersWithSQL);
  /** `(visualId, records, label?)` — `label`: see getActiveFilters(). */
  ctx.updateFilter = queued(updateFilter);
  ctx.removeFilter = queued(removeFilter);
  ctx.resetFilters = queued(dropStaleViews ? resetFiltersDroppingViews : resetFilters);

  /** Rows of the context table as objects keyed by column name. */
  ctx.getFilteredKeys = async () => {
    const c = getConn();
    try {
      const res = await c.query(`SELECT ${keyList} FROM ${contextTable}`);
      if (res?.getColumn) {
        const cols = keys.map((k) => res.getColumn(k.name)?.toArray() ?? []);
        const n = cols.length ? cols[0].length : 0;
        const out = new Array(n);
        for (let i = 0; i < n; i++) {
          const row = {};
          keys.forEach((k, ci) => {
            row[k.name] = cols[ci][i];
          });
          out[i] = row;
        }
        return out;
      }
      const rows = Array.isArray(res) ? res : (res?.data ?? []);
      return rows.map((r) => Object.fromEntries(keys.map((k) => [k.name, r[k.name]])));
    } catch (error) {
      console.error(`Error getting filtered keys for "${id}":`, error);
      return [];
    }
  };

  /** @deprecated alias kept for the sibling repos' drifted copies. */
  ctx.getFilteredRecords = ctx.getFilteredKeys;

  if (keys.length === 1) {
    const only = keys[0].name;
    /** @deprecated single-key alias; use getFilteredKeys(). */
    ctx.getFilteredIds = async () => (await ctx.getFilteredKeys()).map((r) => r[only]);
  }

  ctx.executeFilteredQuery = async (sql) => {
    const c = getConn();
    const ts = ctx.filtersLastUpdated.value.getTime();
    try {
      return await c.query(`
        SELECT q.*
        FROM (${sql}) AS q
        INNER JOIN ${contextTable} fc USING(${keyList})
        /* ${ts} */
      `);
    } catch (error) {
      console.error('Error executing filtered query:', error);
      throw error;
    }
  };

  /**
   * The registered filters in registration order, for display (asset-management's
   * ActiveFiltersBar chips: `${label} (${count})`): `{ visualId, label, count }` — `label` as the
   * registering call passed it (null when it passed none), `count` the match count converted to a
   * JS Number (the `filters` registry keeps DuckDB's BigInt). A fresh snapshot per call, not
   * reactive: re-read it when `filtersLastUpdated` ticks. Read-only — never queued.
   * @returns {ActiveFilter[]}
   */
  ctx.getActiveFilters = () =>
    Object.keys(ctx.filters).map((visualId) => ({
      visualId,
      label: labels.has(visualId) ? labels.get(visualId) : null,
      count: Number(ctx.filters[visualId]),
    }));

  // Unchanged by labels (see getActiveFilters) — identical output with or without them.
  ctx.getFilterDebugInfo = () => ({
    id,
    contextTable,
    options: ctx.options,
    activeFilters: Object.keys(ctx.filters).length,
    filtersByVisual: Object.entries(ctx.filters).map(([vid, count]) => ({
      visualId: vid,
      matchCount: count,
      hasQuery: !!ctx.filterQueries[vid],
    })),
    lastUpdated: ctx.filtersLastUpdated.value,
  });

  return ctx;
}
